import { FabricObject } from 'fabric';

/** Async factory that builds a fabric object for a dynamic-content item, or null to skip it. */
export type DynamicItemFactory<T> = (item: T, index: number) => Promise<FabricObject | null>;

/**
 * Render-side projection of a per-layer dynamic-content array (`dynamic-content-layer-{N}`) into
 * fabric objects — the shared engine behind `GraphicOverlay` (images) and, later,
 * `canvas-textboard-slides` (text). It is a PROJECTION, not the append/reset write path: those writes
 * happen outside the renderer (extension-slide / integrated-hotspot triggers → `ClassroomFacade`),
 * and this reconciles the resulting synced array into canvas objects.
 *
 * Semantics match the channel: **append** (the array grows → a new object is built) and **reset**
 * (the array empties → objects are dropped). Objects are keyed by index + a per-item key (image URL),
 * so a stale async load that resolves after a reset/replace is discarded. **Late-join is free** — the
 * full array is projected on first reconcile.
 *
 * Object creation is async (image load), so {@link reconcile} kicks loads and returns immediately;
 * each completion calls `onLoaded` so the host repaints and picks up the now-ready object via
 * {@link objects}. `reconcile` itself never calls `onLoaded` (it is invoked from the host's
 * after-render pass — a synchronous repaint request there would loop).
 */
export class DynamicContentSource<T> {
    /** Loaded objects by append index; `null` while an item at that index is still loading. */
    private readonly objectsByIndex: (FabricObject | null)[] = [];
    /** Per-index item key (image URL) the current object/load corresponds to. */
    private readonly keys: string[] = [];
    /** Per-index load generation; a resolving load only writes back if its token is still current. */
    private readonly tokens: number[] = [];
    private tokenCounter = 0;

    constructor(
        private readonly factory: DynamicItemFactory<T>,
        private readonly keyOf: (item: T) => string,
        private readonly onLoaded: () => void,
    ) {}

    /**
     * Reconcile the loaded object set to `items` (the live signal value). Truncates on shrink/reset,
     * (re)loads new or changed indices. Cheap + idempotent — safe to call every frame.
     */
    reconcile(items: readonly T[]): void {
        if (items.length < this.objectsByIndex.length) {
            this.objectsByIndex.length = items.length;
            this.keys.length = items.length;
            this.tokens.length = items.length;
        }
        for (let i = 0; i < items.length; i++) {
            const key = this.keyOf(items[i]);
            if (this.keys[i] === key) continue; // already loaded or loading for this key
            this.keys[i] = key;
            this.objectsByIndex[i] = null;
            const token = ++this.tokenCounter;
            this.tokens[i] = token;
            const index = i;
            void this.factory(items[i], i).then(object => {
                if (this.tokens[index] !== token) return; // superseded by a reset/replace
                this.objectsByIndex[index] = object;
                this.onLoaded();
            });
        }
    }

    /** Currently-loaded objects in append order (items still loading are omitted). */
    objects(): FabricObject[] {
        return this.objectsByIndex.filter((o): o is FabricObject => o !== null);
    }

    /** Drop all tracked objects/loads (the host removes them from the canvas). */
    dispose(): void {
        this.objectsByIndex.length = 0;
        this.keys.length = 0;
        this.tokens.length = 0;
        this.tokenCounter++;
    }
}
